import {SettingsSheet} from "./shared/settings-sheet";
import {activateUpdate, registerServiceWorker} from "./shared/sw-update";
import {UpdatePrompt} from "./shared/update-prompt";
import {
    ClickSoundId,
    DEFAULT_DOWNBEAT_SOUND,
    DEFAULT_SECONDARY_SOUND,
    SoundSection,
    toClickSoundId,
} from "./shared/sounds";
import {Tool} from "./shared/tool";
import {ClickerTool} from "./tools/clicker/clicker-tool";
import {PhraserTool} from "./tools/phraser/phraser-tool";

type ToolId = 'clicker' | 'phraser';

/**
 * The suite shell: owns the header, the Phraser | Clicker tab strip, and the
 * settings sheet (app-level shared click sound selection), and switches the
 * active tool.
 *
 * Switching stops the playing tool, with no warning - playback is ephemeral
 * and a confirm dialog would be hostile. The active tab persists
 * (localStorage `activeTool`), so the PWA reopens on the last-used tool.
 */
class Shell {

    private readonly tools: Record<ToolId, Tool>;
    private readonly tabs: HTMLElement[];
    private activeToolId: ToolId;

    constructor() {
        this.tools = {
            clicker: new ClickerTool(),
            phraser: new PhraserTool(),
        };
        this.tabs = Array.from(document.querySelectorAll<HTMLElement>('.tool-tab'));

        this.activeToolId = this.loadActiveTool();
        this.initialiseTabs();
        this.show(this.activeToolId);

        new SettingsSheet({
            getSelection: (section) => section === 'downbeat'
                ? toClickSoundId(localStorage.getItem('downbeatSound'), DEFAULT_DOWNBEAT_SOUND)
                : toClickSoundId(localStorage.getItem('secondarySound'), DEFAULT_SECONDARY_SOUND),
            onSelect: (section, sound) => this.onSoundSelected(section, sound),
        });
    }

    /** Route a sound pick to the active tool; it persists, swaps, and previews. */
    private onSoundSelected(section: SoundSection, sound: ClickSoundId) {
        this.tools[this.activeToolId].onSoundSelected(section, sound);
    }

    private initialiseTabs() {
        this.tabs.forEach(tab => {
            tab.addEventListener('click', () => this.switchTo(tab.dataset.tool as ToolId));
        });
        // tablist arrow-key navigation, with automatic activation
        document.querySelector('.tool-tabs')!.addEventListener('keydown', (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            if (keyEvent.key !== 'ArrowLeft' && keyEvent.key !== 'ArrowRight') {
                return;
            }
            keyEvent.preventDefault();
            const index = this.tabs.indexOf(keyEvent.target as HTMLElement);
            if (index === -1) {
                return;
            }
            const direction = keyEvent.key === 'ArrowLeft' ? -1 : 1;
            const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
            this.switchTo(next.dataset.tool as ToolId);
            next.focus();
        });
    }

    /** Switch tools: stop the outgoing one (no warning), hide it, show the incoming one. */
    private switchTo(toolId: ToolId) {
        if (toolId === this.activeToolId) {
            return;
        }
        this.tools[this.activeToolId].stop();
        this.activeToolId = toolId;
        localStorage.setItem('activeTool', toolId);
        this.show(toolId);
    }

    /** Sync tab states and panel visibility with the active tool. */
    private show(toolId: ToolId) {
        this.tabs.forEach(tab => {
            const isActive = tab.dataset.tool === toolId;
            tab.classList.toggle('active', isActive);
            tab.setAttribute('aria-selected', '' + isActive);
            tab.tabIndex = isActive ? 0 : -1;
            document.getElementById(tab.getAttribute('aria-controls')!)!.hidden = !isActive;
        });
    }

    private loadActiveTool(): ToolId {
        return localStorage.getItem('activeTool') === 'clicker' ? 'clicker' : 'phraser';
    }
}

new Shell();

// Offline support (PWA). Production builds only: the service worker is
// generated into dist/ by scripts/generate-sw.cjs, and registering it against
// the dev server would fight hot reload. When a new version's worker is
// waiting to activate, the user gets an "Update available" prompt; accepting
// activates the worker and reloads once onto the new version.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        const prompt = new UpdatePrompt();
        registerServiceWorker({
            onUpdateAvailable: (waiting) => prompt.show(() => activateUpdate(waiting)),
        }).catch((error) => console.warn('Service worker registration failed:', error));
    });
}
